import React, { useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import FadeIn from 'react-native-fade-in-image';
import { Text, View, ImageBackground, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import hexToRGBA from 'src/core/utils/hexToRGBA';
import toOpaqueColor from 'src/core/utils/toOpaqueColor';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import type { ApiPage } from 'src/core/models/api/ApiPage';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import { navigatePage } from 'src/presentation/helpers/NavigationHelper';

interface EventDetailLinkProps {
	data: ApiPage;
	theme: Record<string, string>;
	spaceAround?: StyleProp<ViewStyle>;
	/** A tile that stands for something other than the page itself, say one categorie of a day. */
	label?: string;
	/** The colour of that something, where it has one of its own. */
	colour?: string;
	onPress?: () => void;
}

function getPageImage(page: ApiPage): string | undefined {
	// `page.data` may be missing (page without image/content); `'x' in null` throws a TypeError.
	const data = page.data as unknown;
	if (data && typeof data === 'object' && 'page_image' in data) {
		return (data as { page_image?: string }).page_image;
	}
	return undefined;
}

/**
 * The colour a beheerder gave this tile.
 */
function getPageColour(page: ApiPage): string | undefined {
	const data = page.data as unknown;
	if (data && typeof data === 'object' && 'page_color' in data) {
		const colour = (data as { page_color?: string }).page_color;
		return colour && colour.length > 0 ? colour : undefined;
	}
	return undefined;
}

function EventDetailLink({ data, theme, spaceAround, label, colour, onPress }: EventDetailLinkProps) {
	const navigationStore = useNavigationStore();
	const eventStore = useEventStore();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): { img: ViewStyle; overlay: ViewStyle } => {
		const tileSize = Platform.OS === 'android' ? (contentWidth - 96) / 2 : (contentWidth - 110) / 2;

		return {
			img: {
				width: tileSize,
				height: tileSize,
				backgroundColor: Colors.greyBg,
				borderRadius: 3,
			},
			overlay: {
				height: tileSize,
				width: tileSize,
				justifyContent: 'center',
				alignItems: 'center',
				borderRadius: 3,
			},
		};
	}, [contentWidth]);

	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const navigateTo = () => {
		const idPath = typeof data.id_path === 'string' && data.id_path.length > 0 ? data.id_path : String(data.id);
		const pageIDs = idPath.includes('/') ? idPath.split('/') : [idPath];
		const page = eventStore.getPageByID(pageIDs);
		if (!page) return;
		navigationStore.setActiveEventByUuid(page.event_uuid);
		navigatePage(navigation, page.type, page);
		navigationStore.setNotificationOrigin('');
	};

	const pageImage = getPageImage(data);

	return (
		<TouchableOpacity style={{ borderRadius: 3 }} onPress={onPress ?? navigateTo}>
			<View style={[styles.box, spaceAround]}>
				{pageImage ? (
					<FadeIn>
						<ImageBackground borderRadius={3} source={{ uri: pageImage }} style={sizedStyles.img}>
							<View
								style={[
									sizedStyles.overlay,
									{
										backgroundColor: hexToRGBA(theme.header_bg_color ?? Colors.blackPure, 0.5),
									},
								]}
							>
								<Text style={styles.title}>{label ?? data.name}</Text>
							</View>
						</ImageBackground>
					</FadeIn>
				) : (
					<View
						style={[
							sizedStyles.overlay,
							// A last colour that is always there: a theme that dresses its header with an image carries no
							// header_bg_color, and without one the tile fell back to nothing at all, which is a white tile
							// with white text on a white page (#430).
							// A colour of its own wins: a categorie tile carries the colour of that categorie, not the one
							// the day it lives on happens to have (#431).
							{ backgroundColor: colour ?? getPageColour(data) ?? toOpaqueColor(theme.header_bg_color) ?? Colors.secondary },
						]}
					>
						<Text style={styles.title}>{label ?? data.name}</Text>
					</View>
				)}
			</View>
		</TouchableOpacity>
	);
}

export default EventDetailLink;

const styles = StyleSheet.create({
	box: {
		marginBottom: Platform.OS === 'android' ? Dimens.margin16 : Dimens.margin30,
		borderRadius: 3,
	},
	title: {
		textAlign: 'center',
		fontFamily: Fonts.montserratBold,
		color: Colors.white,
		fontSize: Dimens.fontSize17,
		backgroundColor: Colors.blackOverlay,
		// The band keeps the full width of the tile; only the words move off its edges, where a title as long
		// as the tile ("Locatie & Adres") ran into them.
		paddingHorizontal: Dimens.padding4,
	},
});
